import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  t,
  getVatPriceProps,
  triggerTrackingUrl,
  getUTCTimeStamp,
} from '@insight/toolkit-utils';

import Button from '../Button/Button';
import Currency from '../Currency/Currency';
import TextEllipsis from '../TextEllipsis/TextEllipsis';
import { withTranslator } from '../Translator/Translator';
import processProductName from './processProductName';
import PartNumbers from '../PartNumbers/PartNumbers';
import QuantitySelector from '../QuantitySelector/QuantitySelector';
import Tooltip from '../Tooltip/Tooltip';
import { CONTRACT_TIMED_OUT_MESSAGE } from '../AddItemToCartModal/constants';
import Loading from '../Loading/Loading';
import Message from '../Message/Message';

/**
 * Display pertinent information about a single, recommended accessory. This
 * component is designed to be used in a Carousel component.
 */
const RecommendedAccessories = ({
  product,
  onAddToBasket,
  isAccessoryModal,
  itemIndex,
  showPrice,
  showAddToCartButton,
  showAddToBasketButton,
  config,
  asyncPrice,
  richRelevanceSSEComplete,
  isIPSUserWithContract,
  clickTrackingURL,
  categoryType,
  insightPrice,
  priceInclVat,
  showVAT,
  currency,
}) => {
  const [isInBasket, setIsInBasket] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(1); // Added state for quantity

  const {
    price,
    image,
    materialId,
    mfrId,
    cesSpecs,
    contract,
    manufacturerPartNumber,
  } = product;
  const { exclVatProps, inclVatProps } = getVatPriceProps(config.showVAT);
  const displayInclVat = config.showVAT && priceInclVat;

  const asyncItem = asyncPrice ? asyncPrice[materialId] : null;
  const finalPrice = asyncItem ? asyncItem?.webPrice : insightPrice;
  const isContractTimedOut =
    richRelevanceSSEComplete && !asyncItem && isIPSUserWithContract;
  const isPriceAvailable = isIPSUserWithContract
    ? asyncItem
      ? !asyncItem?.callForPrice
      : false
    : true;
  const shouldShowPrice = showPrice && isPriceAvailable;

  const [addToCartState, setAddToCartState] = useState({
    loading: false,
    fulfilled: false,
    rejected: null,
  });

  const name = config.useShortProductNames
    ? processProductName(product.name).title
    : product.name;

  const nameClass = cn('c-rec-product__name f-16', {
    'c-rec-product__name--short': config.useShortProductNames,
  });

  useEffect(() => {
    if (!addToCartState.fulfilled) return;
    // Set timer to remove added to cart message
    const timer = setTimeout(
      () => setAddToCartState({ loading: false, fulfilled: false }),
      10000
    );
    return () => clearTimeout(timer);
  }, [addToCartState.fulfilled]);

  const addToCartHandler = async () => {
    setAddToCartState({ loading: true, fulfilled: false });
    try {
      // RR tracking event
      triggerTrackingUrl(clickTrackingURL);
      // Add resource to cart
      await onAddToBasket(
        [
          {
            clientBrowserDate: getUTCTimeStamp(),
            materialID: materialId,
            quantity,
            contractID: asyncItem?.contractNumber,
          },
        ],
        categoryType
      );
      setAddToCartState({ loading: false, fulfilled: true });
    } catch (err) {
      setAddToCartState({
        loading: false,
        fulfilled: false,
        rejected: err.message,
      });
    }
  };

  const renderAddingToChart = () => {
    if (addToCartState.loading)
      return (
        <div className={'ext-loader-container'}>
          <Loading />
        </div>
      );
    if (addToCartState.fulfilled) {
      return (
        <Message
          className="c-product-tabs__resource__confirmation__message ext-loader"
          type="success"
        >
          {t('Added to cart')}
        </Message>
      );
    }
    return null;
  };

  return (
    <div className="c-rec-product" id="recommendedAccessories">
      <div className="o-grid">
        <div className="o-grid__item u-1/1 t-center">
          <img
            className="c-rec-product__img prod-image"
            src={image}
            alt={name}
          />
        </div>
        <div className="o-grid__item u-1/1 mb-12x f-16">
          <Tooltip
            isInModal
            content={<span className="tooltip-label">{name} </span>}
          >
            <TextEllipsis
              id={`rec_product_${itemIndex}`}
              className={nameClass}
              textElement="h3"
              length={isAccessoryModal ? 45 : 100}
              isInModal={isAccessoryModal}
              dontApplyTrimLogic
            >
              {name}
            </TextEllipsis>
          </Tooltip>
        </div>
        {config?.showPartNumbers && (
          <div className="o-grid__item u-1/1 c-rec-product__parts mb-12">
            <PartNumbers
              insightPart={materialId}
              mfrPart={manufacturerPartNumber}
            />
          </div>
        )}

        <div className="o-grid__item u-1/1">
          <Tooltip
            isInModal
            content={
              <span className="tooltip-label">
                {cesSpecs === '' ? product?.name : cesSpecs}{' '}
              </span>
            }
          >
            <p
              className="text-clamp-ext f-14"
              style={{ WebkitBoxOrient: 'vertical' }}
            >
              {cesSpecs === '' ? product?.name : cesSpecs}
            </p>
          </Tooltip>
        </div>

        {isIPSUserWithContract && (
          <div className="o-grid__item u-1/1">
            <div className="u-text-bold u-margin-bot-tiny">
              {isContractTimedOut ? (
                <div>{t(CONTRACT_TIMED_OUT_MESSAGE)}</div>
              ) : (
                <Tooltip
                  className="tooltip-ips"
                  isInModal
                  content={
                    <span className="tooltip-label">
                      {asyncItem?.abbreviation || asyncItem?.contractName}{' '}
                    </span>
                  }
                >
                  <TextEllipsis
                    id={`rec_product_${itemIndex}`}
                    className={'contract-name'}
                    textElement="p"
                    length={30}
                    isInModal
                  >
                    {asyncItem?.abbreviation || asyncItem?.contractName}
                  </TextEllipsis>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        {shouldShowPrice && (
          <div className="o-grid__item c-rec-product__price mt-12">
            <Currency
              className={cn('u-text-bold', {
                'c-rec-product__price__excl-vat': showVAT,
              })}
              currencyCode={currency || asyncItem.currency}
              value={finalPrice}
              {...exclVatProps}
            />
            {showVAT && (
              <Currency
                className="u-text-bold"
                currencyCode={currency || asyncItem.currency}
                value={priceInclVat}
                {...inclVatProps}
              />
            )}
          </div>
        )}
      </div>
      {/* Place buttons and quantity selector side by side */}
      <div className="o-grid o-grid--gutters-small">
        <div className="o-grid__item u-1/2 u-text-left quantity-container">
          <QuantitySelector
            id="quantity"
            onChange={(newQuantity) => setQuantity(newQuantity)} // Update quantity state on change
            value={quantity}
            size="small"
          />
        </div>
        {showAddToBasketButton && (
          <div className="o-grid__item u-1/2 u-text-right btn_alignment">
            <Button
              className="c-rec-product__button expand btn-m"
              color="secondary"
              size="small"
              onClick={addToCartHandler}
              isLoading={isLoading}
              fullWidth={config.fullWidth}
            >
              {t(isInBasket ? 'Added to basket' : 'Add to basket')}
            </Button>
          </div>
        )}
        {showAddToCartButton && (
          <div className="o-grid__item u-1/2 u-text-right btn_alignment">
            {config.isInCart ? (
              <Button
                className="c-rec-product__buttonSuccess btn-m"
                color="link"
                size="small"
              >
                <span className="ion-checkmark-circled">
                  {' '}
                  {t('Added to cart')}
                </span>
              </Button>
            ) : (
              <Button
                className="c-rec-product__button btn-m"
                color="secondary"
                fullWidth={config.fullWidth}
                isLoading={isLoading}
                size="small"
                onClick={addToCartHandler}
              >
                {t('Add to cart')}
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="c-product-tabs__resource__confirmation ext-loader-full">
        {renderAddingToChart()}
      </div>
    </div>
  );
};

/**
 * Wrap the component, ensuring a translator function is provided.
 */
export default withTranslator(RecommendedAccessories);

RecommendedAccessories.propTypes = {
  onAddToBasket: PropTypes.func.isRequired,
  product: PropTypes.shape({
    currency: PropTypes.string,
    href: PropTypes.string,
    image: PropTypes.string,
    materialId: PropTypes.string,
    mfrId: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    cesSpecs: PropTypes.string,
    contract: PropTypes.string,
  }).isRequired,
  showAddToCartButton: PropTypes.bool,
  showAddToBasketButton: PropTypes.bool,
  showPrice: PropTypes.bool,
  useShortProductNames: PropTypes.bool,
  isAccessoryModal: PropTypes.bool,
  itemIndex: PropTypes.number,
  config: PropTypes.object,
  asyncPrice: PropTypes.object,
  richRelevanceSSEComplete: PropTypes.bool,
  isIPSUserWithContract: PropTypes.object,
  clickTrackingURL: PropTypes.string,
  categoryType: PropTypes.string,
  insightPrice: PropTypes.number,
  priceInclVat: PropTypes.number,
  showVAT: PropTypes.bool,
  currency: PropTypes.string,
};

RecommendedAccessories.defaultProps = {
  showAddToBasketButton: false,
  showAddToCartButton: false,
  showPrice: false,
  useShortProductNames: false,
  isIPSUserWithContract: false,
};
